import React, { useState, useEffect, useReducer } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Typography, Dialog, DialogContent, DialogTitle, TextField, Select, MenuItem, CircularProgress } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import CloseIcon from '@mui/icons-material/CloseOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMoreOutlined';

import { PLAYER_ICON_DEFAULT } from '../../../common/staticData';
import { getHeightOptions, getWeightOptions } from '../../../common/utilities';
import { SaveButton, MenuProps } from '../components/common';
import UploadButton from '../components/uploadButton';
import GameService from '../../../services/game.service';
import SeasonContext from '../../../common/Context';
import { setPlayersStatsAdvanceSummaryList, setGoalkeepersStatsAdvanceSummaryList, setPlayersList } from '../../../helpers/coachSlice';
import { getLeagueIds, getGameIds } from '../../../helpers/coach';

const init_player = {
    id: 0,
    f_name: '',
    l_name: '',
    player_name: '',
    date_of_birth: new Date(),
    image: '',
    weight: '',
    height: '',
    strong_foot: '',
    age_group_id: 0,
    age_group_name: '',
    jersey_number: 0,
    position: 0,
    position_name: '',
    second_position: 0,
    second_position_name: '',
    position_role_one: '43',
    position_role_two: '43',
    role_one_name: 'Unknown',
    role_two_name: 'Unknown'
};

const PlayerEditDialog = ({ open, onClose, player, t, onSave = () => {}, updatePlayersList = () => {} }) => {
    // const [selectedDate, setSelectedDate] = React.useState(new Date());
    const [loading, setLoading] = useState(false);

    const { seasonFilter } = React.useContext(SeasonContext);
    const [oldPosition, setOldPosition] = useState('');

    const positions = useSelector((state) => state.coach.positions);
    const roles = useSelector((state) => state.coach.roles);
    const ageGroups = useSelector((state) => state.coach.ageGroups);
    const playersList = useSelector((state) => state.coach.playersList);
    // const leagueList = useSelector((state) => state.coach.leagueList);
    // const gamesList = useSelector((state) => state.coach.gamesList);
    // const playersStatsAdvanceSummaryList = useSelector((state) => state.coach.playersStatsAdvanceSummaryList);
    // const goalkeepersStatsAdvanceSummaryList = useSelector((state) => state.coach.goalkeepersStatsAdvanceSummaryList);

    const dispatch = useDispatch();

    const [values, setValues] = useReducer((old, action) => {
        if (action.age_group_id !== undefined && action.age_group_id !== old.age_group_id) {
            let ageGroupName = '';
            ageGroups.forEach((age) => {
                if (age.id === action.age_group_id) ageGroupName = age.name;
            });
            action.age_group_name = ageGroupName;
        }
        if (action.position_role_one !== undefined && action.position_role_one !== old.position_role_one) {
            let role_name = '';
            roles.forEach((role) => {
                if (role.id === action.position_role_one) role_name = role.role;
            });
            action.role_one_name = role_name;
        }
        if (action.position_role_two !== undefined && action.position_role_two !== old.position_role_two) {
            let role_name = '';
            roles.forEach((role) => {
                if (role.id === action.position_role_two) role_name = role.role;
            });
            action.role_two_name = role_name;
        }
        const newState = { ...old, ...action };
        return newState;
    }, init_player);

    const [roleOneList, setRoleOneList] = useState([]);
    const [roleTwoList, setRoleTwoList] = useState([]);

    // Normalize incoming player prop into local editable state
    const normalizePlayer = (p) => {
        if (!p) return init_player;
        const dob = p.player_date_of_birth || p.date_of_birth ? new Date(p.player_date_of_birth || p.date_of_birth) : new Date();
        const ageGroupId = p.player_age_group_id || p.age_group_id || (p.age_group_name ? (ageGroups.find((age) => age.name === p.age_group_name)?.id || null) : null);
        const firstPosId = p.player_position_id || p.player_position || p.position_id || p.position || null;
        const secondPosId = p.player_second_position_id || p.player_second_position || p.second_position_id || p.second_position || null;
        const firstRoleId = p.player_position_role_one || p.position_role_one_id || p.player_position_role_one_id || p.position_role_one || 43;
        const secondRoleId = p.player_position_role_two || p.position_role_two_id || p.player_position_role_two_id || p.position_role_two || 43;
        const heightVal = p.player_height || p.height || '';
        const weightVal = p.player_weight || p.weight || '';
        return {
            id: p.player_id || p.id || 0,
            f_name: p.player_first_name || p.f_name || '',
            l_name: p.player_last_name || p.l_name || '',
            player_name: p.player_full_name || p.player_name || '',
            date_of_birth: dob,
            image: p.player_image || p.image || '',
            weight: weightVal !== '' ? parseInt(weightVal) : '',
            height: heightVal !== '' ? parseInt(heightVal) : '',
            strong_foot: p.player_strong_foot || p.strong_foot || '',
            age_group_id: ageGroupId || 0,
            age_group_name: p.player_age_group_name || p.age_group_name || '',
            jersey_number: p.player_jersey_number || p.jersey_number || 0,
            position: firstPosId || 0,
            position_name: p.player_position_name || p.position_name || '',
            second_position: secondPosId || 0,
            second_position_name: p.second_position_name || p.player_second_position_name || '',
            position_role_one: String(firstRoleId),
            position_role_two: String(secondRoleId),
            role_one_name: p.position_role_one_name || p.player_position_role_one_name || 'Unknown',
            role_two_name: p.position_role_two_name || p.player_position_role_two_name || 'Unknown'
        };
    };

    // Populate state whenever dialog opens or player/ageGroups change
    useEffect(() => {
        if (!open || !player) return;
        const normalized = normalizePlayer(player);
        setOldPosition(normalized.position_name);
        setValues(normalized);
        setPositionRole(normalized.position);
        setSecondPositionRole(normalized.second_position);
        // Re-apply role ids after lists are built
        setTimeout(() => {
            setValues({ position_role_one: normalized.position_role_one, position_role_two: normalized.position_role_two });
        }, 300);
    }, [open, player, ageGroups]);

    const setPositionRole = (pos_id) => {
        if (pos_id) {
            setRoleOneList(roles.filter((role) => (role.visible && role.position_id === pos_id) || role.id === '43'));
        } else setRoleOneList(roles.filter((role) => role.id === '43'));
        let pos_name = '';
        positions.forEach((pos) => {
            if (pos.id === pos_id) pos_name = pos.name;
        });
        setValues({ position_name: pos_name, position_role_one: '43' });
    };

    const setSecondPositionRole = (pos_id) => {
        if (pos_id) {
            setRoleTwoList(roles.filter((role) => (role.visible && role.position_id === pos_id) || role.id === '43'));
        } else setRoleTwoList(roles.filter((role) => role.id === '43'));
        let pos_name = '';
        positions.forEach((pos) => {
            if (pos.id === pos_id) pos_name = pos.name;
        });
        setValues({ second_position_name: pos_name, position_role_two: '43' });
    };
    const handleChange = (prop) => (e) => {
        if (prop === 'position') {
            setPositionRole(e.target.value);
        } else if (prop === 'second_position') setSecondPositionRole(e.target.value);
        setValues({ [prop]: e.target.value });
    };

    const updatePlayer = async (playerItem) => {
        // if (playersStatsAdvanceSummaryList && playersStatsAdvanceSummaryList.hasOwnProperty(seasonFilter === 'none' ? 'all' : seasonFilter.name)) {
        //     dispatch(
        //         setPlayersStatsAdvanceSummaryList({
        //             season_name: seasonFilter === 'none' ? 'all' : seasonFilter.name,
        //             data: playersStatsAdvanceSummaryList[seasonFilter === 'none' ? 'all' : seasonFilter.name].map((statPlayer) => {
        //                 if (statPlayer.player_id === playerItem.player_id) {
        //                     return { ...statPlayer, ...playerItem };
        //                 }
        //                 return statPlayer;
        //             })
        //         })
        //     );
        //     if (oldPosition === 'Goalkeeper') {
        //         if (playerItem.player_position_name === 'Goalkeeper') {
        //             dispatch(
        //                 setGoalkeepersStatsAdvanceSummaryList({
        //                     season_name: seasonFilter === 'none' ? 'all' : seasonFilter.name,
        //                     data: goalkeepersStatsAdvanceSummaryList[seasonFilter === 'none' ? 'all' : seasonFilter.name].map((statGoalkeeper) => {
        //                         if (statGoalkeeper.player_id === playerItem.player_id) {
        //                             return { ...statGoalkeeper, ...playerItem };
        //                         }
        //                         return statGoalkeeper;
        //                     })
        //                 })
        //             );
        //         } else {
        //             dispatch(
        //                 setGoalkeepersStatsAdvanceSummaryList({
        //                     season_name: seasonFilter === 'none' ? 'all' : seasonFilter.name,
        //                     data: goalkeepersStatsAdvanceSummaryList[seasonFilter === 'none' ? 'all' : seasonFilter.name].filter((statGoalkeeper) => statGoalkeeper.player_id !== playerItem.player_id)
        //                 })
        //             );
        //         }
        //     } else {
        //         if (playerItem.player_position_name === 'Goalkeeper') {
        //             let leagueIds = getLeagueIds(leagueList);
        //             let gameIds = getGameIds(gamesList, seasonFilter.id);
        //             let tmp_goalkeepers = [...goalkeepersStatsAdvanceSummaryList[seasonFilter === 'none' ? 'all' : seasonFilter.name]];
        //             await GameService.getGoalkeepersStatsAdvanceSummary({
        //                 seasonId: seasonFilter === 'none' ? null : seasonFilter.id,
        //                 leagueId: leagueIds.length > 0 ? leagueIds.join(',') : null,
        //                 gameId: gameIds.length > 0 ? gameIds.join(',') : null,
        //                 teamId: playerItem.team_id,
        //                 playerId: playerItem.player_id,
        //                 gameTime: null,
        //                 courtAreaId: null,
        //                 insidePaint: null,
        //                 homeAway: null,
        //                 gameResult: null
        //             }).then((res) => {
        //                 tmp_goalkeepers.push(res[0]);
        //                 dispatch(
        //                     setGoalkeepersStatsAdvanceSummaryList({
        //                         season_name: seasonFilter === 'none' ? 'all' : seasonFilter.name,
        //                         data: tmp_goalkeepers
        //                     })
        //                 );
        //             });
        //         }
        //     }
        // }
    };
    const convertEmptyStringsToNull = (obj) => {
        const result = {};
        for (let key in obj) {
            if (obj.hasOwnProperty(key)) {
                result[key] = obj[key] === '' ? null : obj[key];
            }
        }
        return result;
    };
    const saveChanges = async () => {
        setLoading(true);
        const playerId = player?.player_id || player?.id || values?.id;

        try {
            // Save player changes to server
            const saveData = {
                ...convertEmptyStringsToNull(values),
                weight: values.weight === '' ? null : values.weight,
                height: values.height === '' ? null : values.height,
                // Convert "None" selections (0) to null for nullable ID fields
                position: values.position === 0 ? null : values.position,
                second_position: values.second_position === 0 ? null : values.second_position,
                age_group_id: values.age_group_id === 0 ? null : values.age_group_id
            };
            await GameService.updatePlayer(saveData);

            // Fetch fresh player data from server
            let freshData = null;
            try {
                freshData = await GameService.getPlayerById(playerId);
            } catch (fetchError) {
                console.warn('Could not fetch fresh player data, using local values:', fetchError);
            }

            // Helper to get first non-null/undefined value
            const first = (...vals) => vals.find(v => v !== undefined && v !== null);

            // Helper to get position ID (handle multiple field name patterns)
            const getPositionId = (data, formVal, origPlayer, isPrimary = true) => {
                if (isPrimary) {
                    return first(
                        data?.player_position_id, data?.player_position, data?.position_id, data?.position,
                        formVal,
                        origPlayer?.player_position_id, origPlayer?.position_id
                    );
                }
                return first(
                    data?.player_second_position_id, data?.player_second_position, data?.second_position_id, data?.second_position,
                    formVal,
                    origPlayer?.player_second_position_id, origPlayer?.second_position_id
                );
            };

            // Helper to convert date to ISO string for Redux serialization
            const toDateString = (val) => {
                if (!val) return null;
                if (typeof val === 'string') return val;
                try { return new Date(val).toISOString(); } catch { return null; }
            };

            // Build position values with proper fallbacks
            const positionId = getPositionId(freshData, values?.position, player, true);
            const secondPositionId = getPositionId(freshData, values?.second_position, player, false);
            const positionName = first(freshData?.player_position_name, freshData?.position_name, values?.position_name, player?.player_position_name, player?.position_name) || '';
            const secondPositionName = first(freshData?.player_second_position_name, freshData?.second_position_name, values?.second_position_name, player?.player_second_position_name, player?.second_position_name) || '';

            // Get age group name from ID if not provided
            const ageGroupName = first(
                freshData?.player_age_group_name, freshData?.age_group_name,
                ageGroups.find(a => a.id === values?.age_group_id)?.name,
                player?.player_age_group_name, player?.age_group_name
            ) || '';

            // Build updated player object - start with original player to preserve all fields
            const updatedPlayer = {
                ...player,
                // Core identification
                player_id: playerId,
                id: playerId,
                // Personal info
                player_full_name: first(freshData?.player_full_name, (values?.f_name?.trim() + ' ' + values?.l_name?.trim()), player?.player_full_name) || '',
                player_first_name: first(freshData?.player_first_name, freshData?.f_name, values?.f_name?.trim(), player?.player_first_name) || '',
                player_last_name: first(freshData?.player_last_name, freshData?.l_name, values?.l_name?.trim(), player?.player_last_name) || '',
                player_date_of_birth: toDateString(first(freshData?.player_date_of_birth, freshData?.date_of_birth, values?.date_of_birth, player?.player_date_of_birth)),
                player_jersey_number: first(freshData?.player_jersey_number, freshData?.jersey_number, values?.jersey_number, player?.player_jersey_number),
                player_image: first(freshData?.player_image, freshData?.image, values?.image, player?.player_image) || '',
                // Physical attributes
                player_weight: first(freshData?.player_weight, freshData?.weight, values?.weight ? parseInt(values?.weight) : null, player?.player_weight),
                player_height: first(freshData?.player_height, freshData?.height, values?.height ? parseInt(values?.height) : null, player?.player_height),
                player_strong_foot: first(freshData?.player_strong_foot, freshData?.strong_foot, values?.strong_foot, player?.player_strong_foot) || '',
                player_age_group_id: first(freshData?.player_age_group_id, freshData?.age_group_id, values?.age_group_id, player?.player_age_group_id),
                player_age_group_name: ageGroupName,
                // Position info
                player_position_id: positionId,
                player_position_name: positionName,
                player_second_position_id: secondPositionId,
                player_second_position_name: secondPositionName,
                // Role info
                player_position_role_one_id: first(freshData?.player_position_role_one_id, freshData?.position_role_one_id, values?.position_role_one, player?.player_position_role_one_id) || 43,
                player_position_role_two_id: first(freshData?.player_position_role_two_id, freshData?.position_role_two_id, values?.position_role_two, player?.player_position_role_two_id) || 43,
                player_position_role_one_name: first(freshData?.player_position_role_one_name, freshData?.position_role_one_name, values?.role_one_name, player?.player_position_role_one_name) || 'Unknown',
                player_position_role_two_name: first(freshData?.player_position_role_two_name, freshData?.position_role_two_name, values?.role_two_name, player?.player_position_role_two_name) || 'Unknown',
                // Duplicate top-level fields for filtering and table display
                position_id: positionId,
                position_name: positionName,
                second_position_id: secondPositionId,
                second_position_name: secondPositionName,
                height: first(freshData?.player_height, freshData?.height, values?.height ? parseInt(values?.height) : null, player?.height),
                weight: first(freshData?.player_weight, freshData?.weight, values?.weight ? parseInt(values?.weight) : null, player?.weight),
                strong_foot: first(freshData?.player_strong_foot, freshData?.strong_foot, values?.strong_foot, player?.strong_foot) || '',
                age_group_name: ageGroupName,
                position_role_one_name: first(freshData?.player_position_role_one_name, freshData?.position_role_one_name, values?.role_one_name, player?.position_role_one_name) || 'Unknown',
                position_role_two_name: first(freshData?.player_position_role_two_name, freshData?.position_role_two_name, values?.role_two_name, player?.position_role_two_name) || 'Unknown',
                // Preserve team info from original player (never overwrite)
                team_id: player?.team_id,
                team_name: player?.team_name,
                team_image: player?.team_image
            };

            // Filter out undefined and null values to avoid overriding existing data when merging
            // Keep player_id always, but filter out null/undefined for other fields
            // IMPORTANT: Also exclude team-specific fields (team_id, team_name, team_image) because
            // when a player plays for multiple teams, we don't want to overwrite other team entries
            const teamSpecificFields = ['team_id', 'team_name', 'team_image'];
            const cleanedPlayer = Object.fromEntries(
                Object.entries(updatedPlayer).filter(([key, v]) => {
                    // Exclude team-specific fields - they should not be overwritten when merging
                    if (teamSpecificFields.includes(key)) return false;
                    // Always keep identification fields
                    if (key === 'player_id' || key === 'id') return v !== undefined;
                    // Filter out null and undefined for all other fields
                    return v !== undefined && v !== null;
                })
            );

            // Update local table via callback
            updatePlayersList(cleanedPlayer);
            // Also update global Redux playersList so other components reflect change
            if (playersList && playersList.length) {
                const mergedPlayers = playersList.map((p) => (p.player_id === cleanedPlayer.player_id ? { ...p, ...cleanedPlayer } : p));
                dispatch(setPlayersList(mergedPlayers));
            }
            setLoading(false);
            onSave(cleanedPlayer);
            onClose();
        } catch (e) {
            console.error('Error saving player:', e);
            setLoading(false);
        }
    };

    // (Replaced by the `open` + `player` effect above)

    return (
        <Dialog open={open} onClose={onClose} maxWidth="1500px" maxheight="initial">
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px', cursor: 'pointer' }} onClick={onClose}>
                    <CloseIcon sx={{ color: 'red' }} />
                </Box>
            </DialogTitle>
            <DialogContent style={{ display: 'flex', margin: '0 30px', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '24px' }}>
                {loading && (
                    <div style={{ width: '100%', height: '100%', zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <CircularProgress />
                    </div>
                )}
                <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '28px', fontWeight: 700, color: '#1a1b1d' }}>{t('Edit Player')}</Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: '32px' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
                        <UploadButton
                            class_name="upload-player-view"
                            id_name="player-logo"
                            dirName={process.env.REACT_APP_DIR_PLAYER}
                            img={values.image ? (values.image.includes(process.env.REACT_APP_S3_URI) ? values.image : process.env.REACT_APP_S3_URI + values.image) : PLAYER_ICON_DEFAULT}
                            onURL={(url) => {
                                if (url !== PLAYER_ICON_DEFAULT) setValues({ image: url });
                            }}
                        />
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '14px', fontWeight: 600, color: '#1a1b1d', marginLeft: '16px' }}>{t('Age Group')}</Typography>
                            <Select
                                value={values.age_group_id ?? 0}
                                onChange={handleChange('age_group_id')}
                                label=""
                                variant="outlined"
                                IconComponent={ExpandMoreIcon}
                                inputProps={{ 'aria-label': 'Without label' }}
                                MenuProps={MenuProps}
                                sx={{ outline: 'none', height: '30px', width: '140px', '& legend': { display: 'none' }, '& fieldset': { top: 0 } }}
                            >
                                <MenuItem value={0}>
                                    <em>{t('None')}</em>
                                </MenuItem>
                                {ageGroups.map((item, index) => (
                                    <MenuItem key={index} value={item.id}>
                                        {item.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '14px', fontWeight: 600, color: '#1a1b1d', marginLeft: '16px' }}>{t('Height')}</Typography>
                            <Select
                                value={values.height ?? ''}
                                onChange={handleChange('height')}
                                label=""
                                variant="outlined"
                                IconComponent={ExpandMoreIcon}
                                inputProps={{ 'aria-label': 'Without label' }}
                                MenuProps={MenuProps}
                                sx={{ outline: 'none', height: '30px', width: '140px', '& legend': { display: 'none' }, '& fieldset': { top: 0 } }}
                            >
                                <MenuItem value="">
                                    <em>{t('None')}</em>
                                </MenuItem>
                                {getHeightOptions().map((value) => (
                                    <MenuItem key={value} value={value}>
                                        {value} cm
                                    </MenuItem>
                                ))}
                            </Select>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '14px', fontWeight: 600, color: '#1a1b1d', marginLeft: '16px' }}>{t('Weight')}</Typography>
                            <Select
                                value={values.weight ?? ''}
                                onChange={handleChange('weight')}
                                label=""
                                variant="outlined"
                                IconComponent={ExpandMoreIcon}
                                inputProps={{ 'aria-label': 'Without label' }}
                                MenuProps={MenuProps}
                                sx={{
                                    outline: 'none',
                                    height: '30px',
                                    width: '140px',
                                    '& legend': { display: 'none' },
                                    '& fieldset': { top: 0 }
                                }}
                            >
                                <MenuItem value="">
                                    <em>{t('None')}</em>
                                </MenuItem>
                                {getWeightOptions().map((value) => (
                                    <MenuItem key={value} value={value}>
                                        {value} kg
                                    </MenuItem>
                                ))}
                            </Select>
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '14px', fontWeight: 600, color: '#1a1b1d', marginLeft: '16px' }}>{t('Preferred Foot')}</Typography>
                            <Select
                                value={values.strong_foot ?? ''}
                                onChange={handleChange('strong_foot')}
                                label=""
                                variant="outlined"
                                IconComponent={ExpandMoreIcon}
                                inputProps={{ 'aria-label': 'Without label' }}
                                MenuProps={MenuProps}
                                sx={{ outline: 'none', height: '30px', width: '140px', '& legend': { display: 'none' }, '& fieldset': { top: 0 } }}
                            >
                                <MenuItem value="">
                                    <em>{t('None')}</em>
                                </MenuItem>
                                <MenuItem key={0} value="Right">
                                    Right
                                </MenuItem>
                                <MenuItem key={1} value="Left">
                                    Left
                                </MenuItem>
                                <MenuItem key={2} value="Both">
                                    Both
                                </MenuItem>
                            </Select>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '14px', fontWeight: 600, color: '#1a1b1d', marginLeft: '16px' }}>{t('FirstName')}</Typography>
                                <TextField
                                    value={values.f_name}
                                    onChange={handleChange('f_name')}
                                    label=""
                                    inputProps={{ 'aria-label': 'Without label' }}
                                    variant="outlined"
                                    placeholder="First Name"
                                    disabled={true}
                                    sx={{ borderRadius: '10px', height: '48px', width: '300px', '& legend': { display: 'none' }, '& fieldset': { top: 0 } }}
                                />
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '14px', fontWeight: 600, color: '#1a1b1d', marginLeft: '16px' }}>{t('LastName')}</Typography>
                                <TextField
                                    value={values.l_name}
                                    onChange={handleChange('l_name')}
                                    label=""
                                    inputProps={{ 'aria-label': 'Without label' }}
                                    variant="outlined"
                                    placeholder="Last Name"
                                    disabled={true}
                                    sx={{ borderRadius: '10px', height: '48px', width: '300px', '& legend': { display: 'none' }, '& fieldset': { top: 0 } }}
                                />
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '14px', fontWeight: 600, color: '#1a1b1d', marginLeft: '16px' }}>{t('Birthday')}</Typography>
                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                    <DatePicker
                                        label=""
                                        inputFormat="dd-MM-yyyy"
                                        inputProps={{ 'aria-label': 'Without label' }}
                                        variant="outlined"
                                        value={values.date_of_birth}
                                        onChange={(v) => setValues({ date_of_birth: v })}
                                        renderInput={(params) => (
                                            <TextField sx={{ borderRadius: '10px', height: '48px', width: '300px', '& legend': { display: 'none' }, '& fieldset': { top: 0 } }} {...params} />
                                        )}
                                    />
                                </LocalizationProvider>
                            </Box>
                            {/*<Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '14px', fontWeight: 600, color: '#1a1b1d', marginLeft: '16px' }}>{t('Jersey Number')}</Typography>
                                <TextField
                                    value={values.jersey_number}
                                    onChange={handleChange('jersey_number')}
                                    label=""
                                    inputProps={{ 'aria-label': 'Without label' }}
                                    variant="outlined"
                                    disabled={true}
                                    type="number"
                                    placeholder="Jersey Number"
                                    sx={{ borderRadius: '10px', height: '48px', width: '300px', '& legend': { display: 'none' }, '& fieldset': { top: 0 } }}
                                />
                            </Box>*/}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '14px', fontWeight: 600, color: '#1a1b1d', marginLeft: '16px' }}>{t('First Position')}</Typography>
                                <Select
                                    value={values.position ?? 0}
                                    onChange={handleChange('position')}
                                    label=""
                                    variant="outlined"
                                    IconComponent={ExpandMoreIcon}
                                    inputProps={{ 'aria-label': 'Without label' }}
                                    MenuProps={MenuProps}
                                    sx={{ outline: 'none', height: '48px', width: '300px', '& legend': { display: 'none' }, '& fieldset': { top: 0 } }}
                                >
                                    <MenuItem value={0}>
                                        <em>{t('None')}</em>
                                    </MenuItem>
                                    {positions.map((item, index) => (
                                        <MenuItem key={index} value={item.id}>
                                            {item.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '14px', fontWeight: 600, color: '#1a1b1d', marginLeft: '16px' }}>{t('First Role')}</Typography>
                                <Select
                                    value={values.position_role_one ?? '43'}
                                    onChange={handleChange('position_role_one')}
                                    label=""
                                    variant="outlined"
                                    IconComponent={ExpandMoreIcon}
                                    inputProps={{ 'aria-label': 'Without label' }}
                                    MenuProps={MenuProps}
                                    sx={{ outline: 'none', height: '48px', width: '300px', '& legend': { display: 'none' }, '& fieldset': { top: 0 } }}
                                >
                                    <MenuItem value="43">
                                        <em>{t('Unknown')}</em>
                                    </MenuItem>
                                    {roleOneList.filter(item => item.id !== '43').map((item, index) => (
                                        <MenuItem key={index} value={item.id}>
                                            {item.role}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '14px', fontWeight: 600, color: '#1a1b1d', marginLeft: '16px' }}>{t('Second Position')}</Typography>
                                <Select
                                    value={values.second_position ?? 0}
                                    onChange={handleChange('second_position')}
                                    label=""
                                    variant="outlined"
                                    IconComponent={ExpandMoreIcon}
                                    inputProps={{ 'aria-label': 'Without label' }}
                                    MenuProps={MenuProps}
                                    sx={{ outline: 'none', height: '48px', width: '300px', '& legend': { display: 'none' }, '& fieldset': { top: 0 } }}
                                >
                                    <MenuItem value={0}>
                                        <em>{t('None')}</em>
                                    </MenuItem>
                                    {positions.map((item, index) => (
                                        <MenuItem key={index} value={item.id}>
                                            {item.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '14px', fontWeight: 600, color: '#1a1b1d', marginLeft: '16px' }}>{t('Second Role')}</Typography>
                                <Select
                                    value={values.position_role_two ?? '43'}
                                    onChange={handleChange('position_role_two')}
                                    label=""
                                    variant="outlined"
                                    IconComponent={ExpandMoreIcon}
                                    inputProps={{ 'aria-label': 'Without label' }}
                                    MenuProps={MenuProps}
                                    sx={{ outline: 'none', height: '48px', width: '300px', '& legend': { display: 'none' }, '& fieldset': { top: 0 } }}
                                >
                                    <MenuItem value="43">
                                        <em>{t('Unknown')}</em>
                                    </MenuItem>
                                    {roleTwoList.filter(item => item.id !== '43').map((item, index) => (
                                        <MenuItem key={index} value={item.id}>
                                            {item.role}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Box>
                        </Box>
                    </Box>
                </Box>
                <SaveButton onClick={saveChanges} disabled={loading} sx={{ width: '300px', fontSize: '14px' }}>
                    {t('Save changes')}
                </SaveButton>
            </DialogContent>
        </Dialog>
    );
};

export default PlayerEditDialog;
